import { memo, useCallback, useMemo } from "react"
import { ChatCard as ChatCardComponent } from "@/components/ui/chat-card"
import useChatCardData from "@/components/chatCard/useChatCardData"
import { EnhancedChat } from "../ChatList"
import RouteStopDetailsPopover, {
  CarSpeedPopover,
} from "@/components/RouteStopDetailPopover"

type ChatCardContainerProps = {
  chatJid: string
  chatName: string
  chatData: EnhancedChat
  isArchive: boolean
  isActive: boolean
  onClick: (chat: EnhancedChat) => Promise<void>
}

const ChatCardContainer = ({
  chatJid,
  chatName,
  chatData,
  isActive,
  isArchive,
  onClick,
}: ChatCardContainerProps) => {
  const chartCardProps = useChatCardData(chatData)
  const handleClick = useCallback(() => onClick(chatData), [chatData, onClick])

  const popoverContent = useMemo(
    () =>
      chartCardProps.nextStop ? (
        <RouteStopDetailsPopover
          routeStop={chartCardProps.nextStop}
          speedLabel={chartCardProps.speedLabel}
        />
      ) : chartCardProps.speedLabel ? (
        <CarSpeedPopover speedLabel={chartCardProps.speedLabel} />
      ) : undefined,
    [chartCardProps.nextStop, chartCardProps.speedLabel],
  )

  return (
    <ChatCardComponent
      dataTestId={`${chatName}-${chatJid}`}
      name={chatName}
      isActive={isActive}
      isArchive={isArchive}
      onClick={handleClick}
      popoverContent={popoverContent}
      {...chartCardProps}
    />
  )
}

// Custom comparison function for React.memo to prevent unnecessary re-renders
const arePropsEqual = (
  prevProps: ChatCardContainerProps,
  nextProps: ChatCardContainerProps,
): boolean => {
  // Check primitive props first (most likely to change)
  if (
    prevProps.isActive !== nextProps.isActive ||
    prevProps.isArchive !== nextProps.isArchive ||
    prevProps.chatJid !== nextProps.chatJid ||
    prevProps.chatName !== nextProps.chatName ||
    prevProps.onClick !== nextProps.onClick
  ) {
    return false
  }

  // Only check onClick reference if it's different function reference
  // (our memoized handlers should maintain stable references)
  if (prevProps.onClick !== nextProps.onClick) {
    return false
  }

  // Deep compare chatData properties that actually matter for rendering
  const prevData = prevProps.chatData
  const nextData = nextProps.chatData

  // Quick reference check first
  if (prevData === nextData) {
    return true
  }

  return (
    prevData.jid === nextData.jid &&
    prevData.name === nextData.name &&
    prevData.active === nextData.active &&
    prevData.draftMessage === nextData.draftMessage &&
    prevData.isArchived === nextData.isArchived &&
    prevData.isJoinable === nextData.isJoinable &&
    // Compare participants array length (full deep compare would be expensive)
    prevData.participants.length === nextData.participants.length &&
    // Compare groups array length
    prevData.groups.length === nextData.groups.length
  )
}

export default memo(ChatCardContainer, arePropsEqual)
